/**
 * CameraAnimator.js - Provides smooth camera animations and transitions
 *
 * This module handles camera animations including zoom, orbit and complex movement sequences.
 * Uses requestAnimationFrame for smooth transitions and supports easing functions.
 */

import * as THREE from "three";

export class CameraAnimator {
  constructor(camera, controls) {
    if (!camera || !controls) {
      console.error("CameraAnimator: Camera and controls are required");
      throw new Error("CameraAnimator: Camera and controls are required");
    }

    this.camera = camera;
    this.controls = controls;
    this.isAnimating = false;
    this.animations = [];
    this.currentAnimation = null;
    this.animationFrame = null;
    this.defaultDuration = 2000; // Default duration in ms
    this.isChatFocused = false; // Track if chat is currently focused

    // Default camera position and target
    this.defaultPosition = new THREE.Vector3(0.0, 150.0, 110.6);
    this.defaultTarget = new THREE.Vector3(0, 150, 0);

    // Store the original positions for reset functionality
    this.originalPositions = {
      camera: this.camera.position.clone(),
      target: this.controls.target.clone(),
    };

    console.log("CameraAnimator: Initialized with camera and controls");

    // Bind animation method to this instance
    this.animate = this.animate.bind(this);
  }

  /**
   * Check if camera is at default position
   *
   * @returns {boolean} True if camera is at default position
   */
  isAtDefaultPosition() {
    const positionTolerance = 5; // Tolerance for position check (units)
    const targetTolerance = 5; // Tolerance for target check (units)

    const positionDistance = this.camera.position.distanceTo(
      this.defaultPosition,
    );
    const targetDistance = this.controls.target.distanceTo(this.defaultTarget);

    console.log(`Camera position distance from default: ${positionDistance}`);
    console.log(`Camera target distance from default: ${targetDistance}`);

    return (
      positionDistance < positionTolerance && targetDistance < targetTolerance
    );
  }

  /**
   * Transition camera to default position
   *
   * @param {number} duration - Animation duration in ms
   * @returns {Promise} Resolves when animation completes
   */
  transitionToDefaultPosition(duration = 1000) {
    console.log("CameraAnimator: Transitioning to default position");

    return this.animateCamera({
      position: this.defaultPosition.clone(),
      target: this.defaultTarget.clone(),
      duration: duration,
      ease: this.easeInOutQuad,
    });
  }

  /**
   * Performs the bio sequence animation
   * First checks if camera is at default position, and transitions there if not
   *
   * @param {Object} options - Animation options
   * @returns {Promise} Resolves when animation completes
   */
  bioCameraSequence(options = {}) {
    try {
      console.log("CameraAnimator: Starting cinematic bio camera sequence");

      // Cancel any ongoing animations
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      // Check if camera is at default position
      if (!this.isAtDefaultPosition()) {
        console.log(
          "CameraAnimator: Camera not at default position, transitioning first",
        );

        // Return a Promise chain that first transitions to default, then does bio sequence
        return this.transitionToDefaultPosition(1000).then(() => {
          console.log(
            "CameraAnimator: Now at default position, starting bio sequence",
          );
          // Call this method again, but since we're now at default position, it will continue
          return this.bioCameraSequence(options);
        });
      }

      // Make animation longer for more cinematic feel
      const duration = options.duration || 6000; // 6 seconds for the complete sequence

      // Store current camera position and target for returning later
      const startPosition = this.camera.position.clone();
      const startTarget = this.controls.target.clone();

      // Store original positions for later reset
      this.originalPositions = {
        camera: startPosition.clone(),
        target: startTarget.clone(),
      };

      // Get current character eye level - assuming characters have eye level at Y=165
      const characterEyeLevel = 150;

      // Determine if we should stay in final position or return to original
      const stayInPosition = options.stayInPosition || false;

      // Get final position if specified, otherwise use default
      const finalPosition = options.finalPosition || {
        position: new THREE.Vector3(-40, characterEyeLevel, 80), // Negative X to position on right side
        target: new THREE.Vector3(0, characterEyeLevel, 0),
      };

      // Disable controls during animation
      if (this.controls && this.controls.enabled !== undefined) {
        this.controls.enabled = false;
      }

      return new Promise((resolve) => {
        console.log("CameraAnimator: Executing cinematic camera sequence");

        // Enhanced cinematic animation state
        const state = {
          startTime: Date.now(),
          phase: 1, // Now with more phases for cinematic movement
          angle: 0,
          height: 0,
          completed: false,
          stayInPosition: stayInPosition,

          // Camera waypoints for cinematic camera movement
          waypoints: [
            {
              // 0: Starting position
              position: startPosition.clone(),
              target: startTarget.clone(),
              duration: 0,
            },
            {
              // 1: Initial approach - coming from slightly above (on the right side)
              position: new THREE.Vector3(-50, characterEyeLevel, 120), // Negative X to position on right side
              target: new THREE.Vector3(-50, characterEyeLevel, 120),
              duration: duration * 0.15,
            },
            {
              // 2: Close-up face shot - with the specific coordinates
              position: finalPosition.position.clone(),
              target: finalPosition.target.clone(),
              duration: duration * 0.1,
            },
            {
              // 3: Stay in this position if stayInPosition is true
              position: finalPosition.position.clone(),
              target: finalPosition.target.clone(),
              duration: duration * 0.05,
            },
            {
              // 4: Return to original (will be calculated during animation)
              position: startPosition.clone(),
              target: startTarget.clone(),
              duration: duration * 0.15,
            },
          ],

          // Current waypoint indices
          currentWaypoint: 0,
          nextWaypoint: 1,

          // Orbit parameters
          orbit: {
            startTime: 0,
            duration: stayInPosition ? 0 : duration * 0.55, // Skip orbit if staying in position
            distance: {
              start: 55,
              min: 45, // Get closer during orbit
              max: 65, // Move away during orbit
            },
            height: {
              start: characterEyeLevel,
              min: characterEyeLevel,
              max: characterEyeLevel,
            },
            startAngle: 0,
            endAngle: Math.PI * 2, // Full 360° orbit
          },
        };

        // Animation function - cinematic camera movement with requestAnimationFrame
        const animate = () => {
          if (state.completed) {
            return;
          }

          const now = Date.now();
          const elapsed = now - state.startTime;

          // Orbit phase
          if (state.phase === 3) {
            const orbitElapsed = now - state.orbit.startTime;
            const orbitProgress = Math.min(
              orbitElapsed / state.orbit.duration,
              1,
            );

            // Cinematic approach: variable orbit speed and height
            // Starts slow, speeds up in middle, slows down at end
            let angleProgress;
            if (orbitProgress < 0.3) {
              // Slow start (ease-in)
              angleProgress = 0.3 * this.easeInCubic(orbitProgress / 0.3);
            } else if (orbitProgress > 0.7) {
              // Slow end (ease-out)
              const finalProgress = (orbitProgress - 0.7) / 0.3;
              angleProgress =
                0.3 + 0.4 + 0.3 * this.easeOutCubic(finalProgress);
            } else {
              // Faster middle (linear)
              angleProgress = 0.3 + 0.4 * ((orbitProgress - 0.3) / 0.4);
            }

            // Calculate orbit angle with non-linear progression
            const angle =
              state.orbit.startAngle + angleProgress * state.orbit.endAngle;

            // Calculate camera distance - breathe in and out during orbit
            const breathePhase =
              (1 - Math.cos(orbitProgress * Math.PI * 2)) / 2;
            const distance =
              state.orbit.distance.min +
              (state.orbit.distance.max - state.orbit.distance.min) *
                breathePhase;

            // Calculate height - move up and down during orbit
            const heightPhase = (1 - Math.cos(orbitProgress * Math.PI * 4)) / 2; // Faster oscillation
            const height =
              state.orbit.height.min +
              (state.orbit.height.max - state.orbit.height.min) * heightPhase;

            // Calculate camera position with dynamic height and distance
            const x = Math.sin(angle) * distance;
            const z = Math.cos(angle) * distance;

            // Set camera position with smooth movement
            this.camera.position.set(x, height, z);

            // Look slightly down at character for more dramatic angle const lookAtHeight = characterEyeLevel * breathePhase;
            const lookAtHeight = characterEyeLevel * 1;
            this.controls.target.set(0, lookAtHeight, 0);

            this.camera.lookAt(this.controls.target);
            this.controls.update();

            // Move to next phase when complete
            if (orbitProgress >= 1) {
              state.phase = 4;
              state.startTime = now;
              state.currentWaypoint = 3; // Last orbit position
              state.nextWaypoint = 4; // Return to original

              // Save the final orbit position to use as the starting point for the return
              state.waypoints[3].position.set(x, height, z);

              console.log("CameraAnimator: Phase 4 - Cinematic return");
            }
          }
          // Waypoint-based phases (approach and return)
          else {
            const current = state.waypoints[state.currentWaypoint];
            const next = state.waypoints[state.nextWaypoint];
            const phaseDuration = next.duration;

            const phaseProgress = Math.min(elapsed / phaseDuration, 1);

            // Different easing functions for different phases
            let t;
            if (state.phase === 1) {
              // Dramatic approach
              t = this.easeOutCubic(phaseProgress);
            } else if (state.phase === 2) {
              // Slow dramatic pause before orbit
              t = this.easeInOutSine(phaseProgress);
            } else if (state.phase === 4) {
              // Smooth return
              t = this.easeInOutQuad(phaseProgress);
            } else {
              t = phaseProgress;
            }

            // Interpolate camera position and target
            this.camera.position.lerpVectors(
              current.position,
              next.position,
              t,
            );

            this.controls.target.lerpVectors(current.target, next.target, t);

            this.camera.lookAt(this.controls.target);
            this.controls.update();

            // Move to next waypoint or phase
            if (phaseProgress >= 1) {
              state.currentWaypoint = state.nextWaypoint;
              state.nextWaypoint++;
              state.startTime = now;

              // Phase transitions
              if (state.phase === 1 && state.nextWaypoint === 3) {
                state.phase = 2;
                console.log("CameraAnimator: Phase 2 - Close-up");
              } else if (state.phase === 2 && state.nextWaypoint === 4) {
                // If we need to stay in position without orbit, complete the animation
                if (state.stayInPosition) {
                  state.completed = true;

                  // Re-enable controls
                  if (this.controls) {
                    this.controls.enabled = true;
                  }

                  console.log("CameraAnimator: Staying in bio view position");
                  resolve();
                  return;
                }
                // Otherwise proceed to orbit phase
                else {
                  state.phase = 3;
                  state.orbit.startTime = now;
                  console.log("CameraAnimator: Phase 3 - Cinematic orbit");
                }
              } else if (
                state.phase === 4 &&
                state.nextWaypoint >= state.waypoints.length
              ) {
                state.completed = true;

                // Re-enable controls
                if (this.controls) {
                  this.controls.enabled = true;
                }

                console.log("CameraAnimator: Cinematic sequence complete");
                resolve();
                return;
              }
            }
          }

          // Continue animation loop
          this.animationFrame = requestAnimationFrame(animate);
        };

        // Start animation loop
        this.animationFrame = requestAnimationFrame(animate);
      });
    } catch (error) {
      console.error("CameraAnimator: Error in bio camera sequence", error);
      // Re-enable controls if there was an error
      if (this.controls) {
        this.controls.enabled = true;
      }
      return Promise.resolve(); // Return resolved promise to prevent further errors
    }
  }

  /**
   * Animate camera from current position/target to specified position/target
   *
   * @param {Object} options - Animation options
   * @returns {Promise} Resolves when animation completes
   */
  animateCamera(options = {}) {
    const {
      position,
      target,
      duration = this.defaultDuration,
      ease = this.easeInOutQuad,
      onProgress,
      onComplete,
    } = options;

    // Default target to current target if not specified
    const targetPosition = target || this.controls.target.clone();

    // Create animation object
    const animation = {
      startTime: performance.now(),
      duration,
      startPosition: this.camera.position.clone(),
      endPosition: position.clone(),
      startTarget: this.controls.target.clone(),
      endTarget: targetPosition.clone(),
      easeFunction: ease,
      onProgress,
      onComplete,
    };

    // Start animation
    this.startAnimation(animation);

    // Return promise that resolves when animation completes
    return new Promise((resolve) => {
      animation.onComplete = () => {
        if (onComplete) onComplete();
        resolve();
      };
    });
  }

  /**
   * Reset camera to its original stored position
   * Called when the bio panel is closed
   *
   * @returns {Promise} Resolves when reset completes
   */
  resetToOriginalPosition() {
    return new Promise((resolve, reject) => {
      try {
        // Check if we have stored original positions
        if (
          !this.originalPositions ||
          !this.originalPositions.camera ||
          !this.originalPositions.target
        ) {
          console.warn(
            "CameraAnimator: No original positions stored, using default position",
          );

          // Set to a reasonable default position if no original position is available
          this.camera.position.set(0, 150, 110.6);
          this.controls.target.set(0, 150, 0);
          this.camera.lookAt(this.controls.target);
          this.controls.update();

          resolve();
          return;
        }

        console.log("CameraAnimator: Resetting to original position");

        // Current position and target
        const startPosition = this.camera.position.clone();
        const startTarget = this.controls.target.clone();

        // Original position and target to return to
        const endPosition = this.originalPositions.camera.clone();
        const endTarget = this.originalPositions.target.clone();

        // Animation parameters
        const duration = 1000; // 1 second for smooth transition
        const startTime = Date.now();

        // Disable controls during animation
        if (this.controls && this.controls.enabled !== undefined) {
          this.controls.enabled = false;
        }

        // Cancel any ongoing animations
        if (this.animationFrame) {
          cancelAnimationFrame(this.animationFrame);
          this.animationFrame = null;
        }

        // Animation function
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Use easeOutCubic for smoother end of animation
          const t = 1 - Math.pow(1 - progress, 3);

          // Interpolate position
          this.camera.position.lerpVectors(startPosition, endPosition, t);

          // Interpolate target
          this.controls.target.lerpVectors(startTarget, endTarget, t);

          this.camera.lookAt(this.controls.target);
          this.controls.update();

          if (progress < 1) {
            this.animationFrame = requestAnimationFrame(animate);
          } else {
            // Re-enable controls
            if (this.controls) {
              this.controls.enabled = true;
            }

            console.log("CameraAnimator: Reset to original position complete");
            resolve();
          }
        };

        // Start animation
        this.animationFrame = requestAnimationFrame(animate);
      } catch (error) {
        console.error(
          "CameraAnimator: Error resetting to original position",
          error,
        );

        // Re-enable controls if there was an error
        if (this.controls) {
          this.controls.enabled = true;
        }

        reject(error);
      }
    });
  }

  /**
   * Orbit camera around a central point
   *
   * @param {Object} options - Orbit options
   * @returns {Promise} Resolves when orbit completes
   */
  orbitAroundCharacter(options = {}) {
    const {
      duration = this.defaultDuration,
      startAngle = 0,
      endAngle = Math.PI * 2,
      distance = 100,
      height = 165, // Default to character eye level
      ease = this.easeInOutQuad,
      onProgress,
      onComplete,
    } = options;

    // Cancel any ongoing animations
    this.cancelAnimations();

    // Create animation object
    const animation = {
      startTime: performance.now(),
      duration,
      startAngle,
      endAngle,
      distance,
      height,
      easeFunction: ease,
      onProgress,
      onComplete,
      type: "orbit",
    };

    // Start animation
    this.startAnimation(animation);

    // Return promise that resolves when animation completes
    return new Promise((resolve) => {
      animation.onComplete = () => {
        if (onComplete) onComplete();
        resolve();
      };
    });
  }

  /**
   * Start an animation
   *
   * @param {Object} animation - Animation configuration
   */
  startAnimation(animation) {
    // Cancel any ongoing animations
    this.cancelAnimations();

    // Set current animation
    this.currentAnimation = animation;
    this.isAnimating = true;

    // Start animation loop
    if (!this.animationFrame) {
      this.animationFrame = requestAnimationFrame(this.animate);
    }
  }

  /**
   * Animation loop
   *
   * @param {number} timestamp - Current timestamp
   */
  animate(timestamp) {
    if (!this.isAnimating || !this.currentAnimation) {
      this.animationFrame = null;
      return;
    }

    const animation = this.currentAnimation;
    const elapsed = timestamp - animation.startTime;
    const progress = Math.min(elapsed / animation.duration, 1);
    const easedProgress = animation.easeFunction(progress);

    if (animation.type === "orbit") {
      // Orbit animation
      const angle =
        animation.startAngle +
        (animation.endAngle - animation.startAngle) * easedProgress;

      // Calculate new camera position based on orbit angle
      const x = Math.sin(angle) * animation.distance;
      const z = Math.cos(angle) * animation.distance;

      // Update camera position
      this.camera.position.set(x, animation.height, z);

      // Keep looking at the center target
      this.controls.target.set(0, animation.height, 0);
      this.camera.lookAt(this.controls.target);
    } else {
      // Standard position animation
      // Interpolate position
      this.camera.position.lerpVectors(
        animation.startPosition,
        animation.endPosition,
        easedProgress,
      );

      // Interpolate target
      this.controls.target.lerpVectors(
        animation.startTarget,
        animation.endTarget,
        easedProgress,
      );
    }

    // Update controls
    this.controls.update();

    // Call progress callback if provided
    if (animation.onProgress) {
      animation.onProgress(easedProgress);
    }

    // Check if animation is complete
    if (progress >= 1) {
      this.isAnimating = false;

      // Call complete callback if provided
      if (animation.onComplete) {
        animation.onComplete();
      }

      this.currentAnimation = null;
    }

    // Continue animation loop
    if (this.isAnimating) {
      this.animationFrame = requestAnimationFrame(this.animate);
    } else {
      this.animationFrame = null;
    }
  }

  /**
   * Cancel any ongoing animations
   */
  cancelAnimations() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    this.isAnimating = false;
    this.currentAnimation = null;
  }

  /**
   * Reset camera to original position
   *
   * @param {Object} options - Reset options
   * @returns {Promise} Resolves when reset completes
   */
  resetCamera(options = {}) {
    const { duration = 1000, ease = this.easeOutCubic } = options;

    return this.animateCamera({
      position: this.originalPositions.camera,
      target: this.originalPositions.target,
      duration,
      ease,
    });
  }

  /**
   * Ease functions for smooth animations
   */

  // Quadratic ease in/out
  easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  // Cubic ease out
  easeOutCubic(t) {
    return --t * t * t + 1;
  }

  // Cubic ease in
  easeInCubic(t) {
    return t * t * t;
  }

  // Sinusoidal ease in/out
  easeInOutSine(t) {
    return -0.5 * (Math.cos(Math.PI * t) - 1);
  }

  /**
   * Cinematic camera animation for chat interactions
   * Creates a smooth transition to a close-up view when chat is focused
   * and returns to the original position when chat is blurred
   *
   * @param {Boolean} focused - Whether the chat input is focused or not
   * @param {Object} options - Animation options (duration, etc.)
   * @returns {Promise} Resolves when the animation completes
   */
  chatCameraAnimation(focused, options = {}) {
    try {
      console.log(
        `CameraAnimator: Starting cinematic chat camera animation, focused: ${focused}`,
      );

      // Skip if current state matches requested state
      if (this.isChatFocused === focused) {
        console.log(
          "CameraAnimator: Chat focus state unchanged, skipping animation",
        );
        return Promise.resolve();
      }

      // Update chat focus state
      this.isChatFocused = focused;

      // Cancel any ongoing animations
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      // If focusing on chat and not at default position, transition to default first
      if (focused && !this.isAtDefaultPosition()) {
        console.log(
          "CameraAnimator: Camera not at default position before chat focus, transitioning first",
        );

        // Return a Promise chain that first transitions to default, then does chat animation
        return this.transitionToDefaultPosition(1000).then(() => {
          console.log(
            "CameraAnimator: Now at default position, starting chat focus animation",
          );

          // Force chat focus state to be false so we can transition to focused state
          this.isChatFocused = false;

          // Call this method again, but since we're now at default position, it will continue
          return this.chatCameraAnimation(focused, options);
        });
      }

      // Set up animation parameters
      const duration = options.duration || 1500; // 1.5 seconds for the transition
      const characterEyeLevel = 150; // Updated eye level to match new default

      // Store current camera position and target
      const startPosition = this.camera.position.clone();
      const startTarget = this.controls.target.clone();

      // Save original position when first focusing the chat
      if (focused && !this.chatOriginalPosition) {
        this.chatOriginalPosition = {
          camera: startPosition.clone(),
          target: startTarget.clone(),
        };
        console.log(
          "CameraAnimator: Saved chat original camera position for return",
        );
      }

      // Define the end position based on focused state
      let endPosition, endTarget;

      if (focused) {
        // When chat is focused: cinematic leftward movement maintaining Y position
        // Position camera at an angle that frames the character while moving left
        const leftShift = options.leftShift || 35; // Amount to shift left

        // Use our exact Y position for consistency
        const currentYPosition = 150.0;
        const currentZPosition = 110.6;

        // Move camera as far left as possible without exiting the screen
        // Use a more aggressive X value for maximum leftward movement while keeping character visible
        endPosition = new THREE.Vector3(5, currentYPosition, currentZPosition);
        // Set target position to achieve maximum leftward view without character leaving screen
        const maxLeftShift = 20; // Optimal value for maximum leftward shift without losing character
        endTarget = new THREE.Vector3(maxLeftShift, 150.0, 0);

        // Log the movement details
        console.log(
          `CameraAnimator: Moving camera and model to the left by ${leftShift} units while maintaining Y position`,
        );
      } else {
        // When chat is blurred: Return to the original position before chat
        if (this.chatOriginalPosition) {
          // Use the saved original position from when chat was first focused
          endPosition = this.chatOriginalPosition.camera.clone();
          endTarget = this.chatOriginalPosition.target.clone();
          console.log(
            "CameraAnimator: Returning to original camera position before chat",
          );
        } else {
          // Fallback to default position if no original position was saved
          endPosition = this.defaultPosition.clone();
          endTarget = this.defaultTarget.clone();
          console.log(
            "CameraAnimator: No original position found, returning to default (0.0, 150.0, 110.6)",
          );
        }
      }

      // Disable controls during animation
      if (this.controls && this.controls.enabled !== undefined) {
        this.controls.enabled = false;
      }

      return new Promise((resolve) => {
        const startTime = Date.now();

        // Animation function - smooth cinematic camera transition
        const animate = () => {
          const now = Date.now();
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Use different easing for focus vs. blur
          let t;
          if (focused) {
            // Dramatic cinematic move (ease out cubic)
            t = this.easeOutCubic(progress);
          } else {
            // Smooth return (ease in-out quad)
            t = this.easeInOutQuad(progress);
          }

          // Interpolate camera position with smooth cinematic movement
          this.camera.position.lerpVectors(startPosition, endPosition, t);

          // Interpolate target with smooth cinematic movement
          this.controls.target.lerpVectors(startTarget, endTarget, t);

          this.camera.lookAt(this.controls.target);
          this.controls.update();

          // Continue animation until complete
          if (progress < 1) {
            this.animationFrame = requestAnimationFrame(animate);
          } else {
            // Re-enable controls when complete
            if (this.controls) {
              this.controls.enabled = true;
            }

            console.log("CameraAnimator: Chat camera animation complete");
            resolve();
          }
        };

        // Start the animation
        this.animationFrame = requestAnimationFrame(animate);
      });
    } catch (error) {
      console.error("CameraAnimator: Error in chat camera animation", error);
      // Re-enable controls if there was an error
      if (this.controls) {
        this.controls.enabled = true;
      }
      return Promise.resolve(); // Return resolved promise to prevent further errors
    }
  }
}
